import { CpmButton, CpmToggleSwitch } from '../../../../components/design-system';
import {
    CPM_STATE_FILTER_TABS,
    formatStoreCounts,
} from '../../../../constants/centralProductMaster.constants';
import type { CpmState, CpmStateFilterTab } from '../../../../types/centralProductMaster.types';

interface StateListTableProps {
    states: CpmState[];
    stateQuery: string;
    stateFilter: CpmStateFilterTab;
    onStateQueryChange: (q: string) => void;
    onStateFilterChange: (tab: CpmStateFilterTab) => void;
    onClearFilters: () => void;
    onOpenState: (stateName: string) => void;
    onToggleLive: (state: CpmState) => void;
    liveFailName?: string | null;
}

type Tone = 'ok' | 'warn' | 'off';

function toneFor(s: CpmState): Tone {
    if (!s.isLive) return 'off';
    if ((s.categoryCount || 0) > 0) return 'ok';
    return 'warn';
}

function rank(s: CpmState): number {
    const n = s.categoryCount || 0;
    if (s.isLive && !n) return 0;
    if (s.isLive) return 1;
    return 2;
}

const GRID = 'minmax(0,1.4fr) 114px 96px 92px minmax(0,1fr) 46px 22px';

export default function StateListTable({
    states,
    stateQuery,
    stateFilter,
    onStateQueryChange,
    onStateFilterChange,
    onClearFilters,
    onOpenState,
    onToggleLive,
    liveFailName = null,
}: StateListTableProps) {
    const liveStates = states.filter((s) => s.isLive);
    const pending = liveStates.filter((s) => (s.categoryCount || 0) === 0);
    const curated = liveStates.filter((s) => (s.categoryCount || 0) > 0).length;
    const allGood = pending.length === 0;

    const counts: Record<CpmStateFilterTab, number> = {
        all: states.length,
        live: liveStates.length,
        pending: pending.length,
        off: states.length - liveStates.length,
    };

    const sq = stateQuery.trim().toLowerCase();
    let rows = states.slice().sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
    if (stateFilter === 'live') rows = rows.filter((x) => x.isLive);
    else if (stateFilter === 'pending')
        rows = rows.filter((x) => x.isLive && (x.categoryCount || 0) === 0);
    else if (stateFilter === 'off') rows = rows.filter((x) => !x.isLive);
    if (sq) rows = rows.filter((x) => x.name.toLowerCase().includes(sq));

    const heroChipLabel = allGood
        ? 'All live states curated'
        : pending.length > 2
          ? `${pending.length} live states need setup`
          : `${pending.map((p) => p.name).join(', ')} needs setup`;
    const heroNote = allGood
        ? 'Every live state has categories, so nothing is being skipped. Come back when a state is marked live.'
        : 'Linked variants in those states get no state-product pricing or excise rows until each list is curated. The daily job retries once they\u2019re ready.';

    return (
        <>
            <div
                className="rounded-[16px] flex items-end justify-between flex-wrap"
                style={{
                    backgroundImage: 'var(--cpm-gradient-hero)',
                    boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.28)',
                    padding: '26px 28px',
                    gap: 32,
                }}
            >
                <div className="flex flex-col max-w-[660px]" style={{ gap: 8 }}>
                    <div
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'rgba(255,255,255,.7)',
                        }}
                    >
                        Central Product Master
                    </div>
                    <h1 className="m-0 !text-[30px] !font-extrabold !tracking-[-0.025em] !leading-[1.05] !text-white">
                        Excise Category Curation
                    </h1>
                    <p
                        className="m-0 !text-[14px] !font-medium !leading-[1.5]"
                        style={{ color: 'rgba(255,255,255,.72)' }}
                    >
                        Every state&apos;s excise department names its liquor categories its own
                        way. Set up each state&apos;s list by hand, one state at a time. Until a
                        state is marked live and has at least one curated category, the linking
                        pipeline creates no state-product pricing or excise rows for that
                        state&apos;s linked variants.
                    </p>
                </div>
                <div className="flex items-center flex-wrap" style={{ gap: 20 }}>
                    <div className="flex flex-col" style={{ gap: 5 }}>
                        <div
                            className="uppercase"
                            style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                color: 'rgba(255,255,255,.6)',
                            }}
                        >
                            Live states curated
                        </div>
                        <div
                            className="tabular text-white"
                            style={{
                                fontSize: 38,
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                lineHeight: 1,
                            }}
                        >
                            {curated} of {liveStates.length}
                        </div>
                    </div>
                    <div
                        className="self-stretch"
                        style={{ width: 1, background: 'rgba(255,255,255,.18)' }}
                    />
                    <div className="flex flex-col max-w-[230px]" style={{ gap: 8 }}>
                        <div
                            className="inline-flex items-center rounded-full text-white"
                            style={{
                                gap: 7,
                                padding: '5px 11px',
                                background: 'rgba(255,255,255,.12)',
                                border: '1px solid rgba(255,255,255,.2)',
                                fontSize: 11.5,
                                fontWeight: 800,
                            }}
                        >
                            <span
                                className="rounded-full"
                                style={{
                                    width: 7,
                                    height: 7,
                                    background: allGood ? '#4ADE80' : '#FBBF24',
                                    boxShadow: allGood
                                        ? '0 0 0 3px rgba(74,222,128,.25)'
                                        : '0 0 0 3px rgba(251,191,36,.25)',
                                }}
                            />
                            {heroChipLabel}
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                lineHeight: 1.4,
                                color: 'rgba(255,255,255,.65)',
                            }}
                        >
                            {heroNote}
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)] flex flex-col"
                style={{
                    boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                    padding: 20,
                    gap: 16,
                }}
            >
                <div className="flex items-center flex-wrap" style={{ gap: 16 }}>
                    <div className="flex flex-col min-w-[220px]" style={{ gap: 4 }}>
                        <h2 className="m-0 !text-[15px] !font-extrabold !tracking-[-0.01em] !text-[var(--cpm-brand-navy)]">
                            States
                        </h2>
                        <span
                            style={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            Open a state to curate its excise categories.{' '}
                            <span style={{ color: 'var(--cpm-text-tertiary)' }}>
                                Live plus at least one curated category is what lets state-product
                                rows be created automatically.
                            </span>
                        </span>
                    </div>
                    <div className="ml-auto flex items-center flex-wrap" style={{ gap: 10 }}>
                        <div
                            className="inline-flex items-center rounded-[10px] bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                            style={{ gap: 3, padding: 3 }}
                        >
                            {CPM_STATE_FILTER_TABS.map((t) => {
                                const on = stateFilter === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => onStateFilterChange(t.key)}
                                        className="inline-flex items-center rounded-[8px] transition-all duration-150"
                                        style={{
                                            gap: 7,
                                            height: 32,
                                            padding: '0 12px',
                                            fontSize: 12.5,
                                            fontWeight: 800,
                                            background: on ? '#fff' : 'transparent',
                                            color: on
                                                ? 'var(--cpm-brand-navy)'
                                                : 'var(--cpm-text-secondary)',
                                            boxShadow: on ? '0 1px 2px rgba(14,8,88,.12)' : 'none',
                                            cursor: 'pointer',
                                            border: 'none',
                                        }}
                                    >
                                        {t.label}
                                        <span
                                            className="tabular"
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                color: on
                                                    ? 'var(--cpm-brand-purple)'
                                                    : 'var(--cpm-text-tertiary)',
                                            }}
                                        >
                                            {counts[t.key]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <span
                            className="inline-flex items-center rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white"
                            style={{ gap: 8, height: 38, padding: '0 12px' }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="var(--cpm-text-tertiary)"
                                strokeWidth="2.2"
                                className="shrink-0"
                            >
                                <circle cx="11" cy="11" r="7" />
                                <path d="m21 21-4.3-4.3" />
                            </svg>
                            <input
                                value={stateQuery}
                                onChange={(e) => onStateQueryChange(e.target.value)}
                                placeholder="Find a state"
                                className="border-none outline-none bg-transparent text-[var(--cpm-brand-navy)]"
                                style={{ width: 150, fontSize: 13, fontWeight: 700 }}
                            />
                        </span>
                    </div>
                </div>

                <div className="flex flex-col">
                    <div
                        className="cpm-table-head grid items-center border-b border-[var(--cpm-border-default)]"
                        style={{
                            gridTemplateColumns: GRID,
                            gap: 14,
                            padding: '0 14px 9px',
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-primary)',
                            fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                        }}
                    >
                        <span>State</span>
                        <span>Curation</span>
                        <span>Categories</span>
                        <span>License types</span>
                        <span>Stores active / trial</span>
                        <span className="justify-self-end">Live</span>
                        <span />
                    </div>

                    {rows.map((s) => {
                        const tone = toneFor(s);
                        const stores = (s.activeStoreCount || 0) + (s.trialStoreCount || 0);
                        const catN = s.categoryCount || 0;
                        const subN = s.subCategoryCount || 0;
                        const typeN = s.licenseTypes?.length ?? 0;
                        const catLabel = catN ? `${catN}${subN ? ` · ${subN} sub` : ''}` : '—';
                        const statusLabel =
                            tone === 'ok'
                                ? 'Curated'
                                : tone === 'warn'
                                  ? 'Needs setup'
                                  : 'Not live';
                        const dot =
                            tone === 'ok'
                                ? 'var(--cpm-success-dot)'
                                : tone === 'warn'
                                  ? 'var(--cpm-warning-dot)'
                                  : 'var(--cpm-text-tertiary)';

                        return (
                            <div
                                key={s._id}
                                role="button"
                                tabIndex={0}
                                onClick={() => onOpenState(s.name)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpenState(s.name);
                                    }
                                }}
                                className="grid items-center rounded-[10px] cursor-pointer transition-colors duration-150 hover:bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                                style={{
                                    gridTemplateColumns: GRID,
                                    gap: 14,
                                    padding: '13px 14px',
                                    background:
                                        tone === 'warn' ? 'var(--cpm-warning-bg)' : 'transparent',
                                }}
                            >
                                <div className="flex items-center min-w-0" style={{ gap: 11 }}>
                                    <span
                                        className="shrink-0 rounded-full"
                                        style={{
                                            width: 9,
                                            height: 9,
                                            background: dot,
                                            boxShadow: `0 0 0 3px ${
                                                tone === 'ok'
                                                    ? 'var(--cpm-success-bg)'
                                                    : tone === 'warn'
                                                      ? '#fff'
                                                      : 'var(--cpm-surface-subtle)'
                                            }`,
                                        }}
                                    />
                                    <span
                                        className="truncate text-[var(--cpm-text-primary)]"
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {s.name}
                                    </span>
                                </div>
                                <span
                                    className="inline-flex items-center w-fit rounded-full"
                                    style={{
                                        gap: 6,
                                        padding: '3px 9px',
                                        fontSize: 11,
                                        fontWeight: 800,
                                        background:
                                            tone === 'ok'
                                                ? 'var(--cpm-success-bg)'
                                                : tone === 'warn'
                                                  ? '#fff'
                                                  : 'var(--cpm-surface-subtle)',
                                        border: `1px solid ${
                                            tone === 'ok'
                                                ? 'var(--cpm-success-border)'
                                                : tone === 'warn'
                                                  ? 'var(--cpm-warning-border)'
                                                  : 'var(--cpm-border-default)'
                                        }`,
                                        color:
                                            tone === 'ok'
                                                ? 'var(--cpm-success-text-light)'
                                                : tone === 'warn'
                                                  ? 'var(--cpm-warning-text)'
                                                  : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    <span
                                        className="rounded-full"
                                        style={{ width: 6, height: 6, background: dot }}
                                    />
                                    {statusLabel}
                                </span>
                                <span
                                    className="tabular"
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 800,
                                        color: catN
                                            ? 'var(--cpm-text-primary)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {catLabel}
                                </span>
                                <span
                                    className="tabular"
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: typeN ? 800 : 700,
                                        color: typeN
                                            ? 'var(--cpm-text-primary)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {typeN} types
                                </span>
                                <span
                                    className="tabular truncate"
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: stores
                                            ? 'var(--cpm-text-secondary)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {formatStoreCounts(
                                        s.activeStoreCount || 0,
                                        s.trialStoreCount || 0
                                    )}
                                </span>
                                <span
                                    className="justify-self-end inline-flex flex-col items-end"
                                    style={{ gap: 4 }}
                                >
                                    <CpmToggleSwitch
                                        checked={!!s.isLive}
                                        onChange={() => onToggleLive(s)}
                                        title={
                                            s.isLive
                                                ? stores
                                                    ? 'Marking not live is blocked while stores are operating'
                                                    : `Mark ${s.name} not live`
                                                : `Mark ${s.name} live`
                                        }
                                    />
                                    {liveFailName === s.name ? (
                                        <button
                                            type="button"
                                            className="border-0 bg-transparent p-0 cursor-pointer text-[var(--cpm-shortfall-text)]"
                                            style={{ fontSize: 10.5, fontWeight: 800 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleLive(s);
                                            }}
                                        >
                                            Couldn&apos;t update — retry
                                        </button>
                                    ) : null}
                                </span>
                                <span className="inline-flex items-center justify-center text-[var(--cpm-text-tertiary)]">
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                    >
                                        <path d="m9 18 6-6-6-6" />
                                    </svg>
                                </span>
                            </div>
                        );
                    })}
                </div>

                {rows.length === 0 ? (
                    <div
                        className="rounded-[10px] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] flex flex-col items-center text-center"
                        style={{ padding: 22, gap: 8 }}
                    >
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 13.5, fontWeight: 800 }}
                        >
                            No state matches that filter
                        </span>
                        <CpmButton variant="outline" size="sm" onClick={onClearFilters}>
                            Show all states
                        </CpmButton>
                    </div>
                ) : null}
            </div>
        </>
    );
}
